import { EventEmitter } from '../events/MapEvent';

export class Interaction extends EventEmitter {
  protected active: boolean = true;
  protected map: any;
  protected _handlers: Array<{ event: string; fn: (e: any) => void }> = [];

  setActive(active: boolean): void { this.active = active; }
  isActive(): boolean { return this.active; }

  setMap(map: any): void {
    this.detach();
    this.map = map;
    if (map) this.attach();
  }

  protected attach(): void { /* override */ }

  protected detach(): void {
    const ml = this.map?._maplibreMap;
    if (ml) {
      for (const h of this._handlers) { try { ml.off(h.event, h.fn); } catch {} }
    }
    this._handlers = [];
  }

  /** @internal — subscribe to a raw maplibre event for the lifetime of this interaction. */
  protected _listenML(event: string, fn: (e: any) => void): void {
    this.map?._maplibreMap?.on(event, fn);
    this._handlers.push({ event, fn });
  }

  destroy(): void { this.detach(); this.removeAllListeners(); }
}

export interface SelectInteractionOptions {
  layers?: any[];
  multi?: boolean;
  condition?: 'click' | 'hover' | 'pointerdown';
  hitTolerance?: number;
}

export class SelectInteraction extends Interaction {
  private selected: any[] = [];
  private options: SelectInteractionOptions;

  constructor(options: SelectInteractionOptions = {}) {
    super();
    this.options = { multi: false, condition: 'click', hitTolerance: 2, ...options };
  }

  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    const event = this.options.condition === 'hover' ? 'mousemove'
      : this.options.condition === 'pointerdown' ? 'mousedown' : 'click';
    this._listenML(event, (e: any) => {
      if (!this.active) return;
      const layerIds = (this.options.layers || []).map((l: any) => typeof l === 'string' ? l : l.id).filter(Boolean);
      const features = layerIds.length
        ? ml.queryRenderedFeatures(e.point, { layers: layerIds })
        : ml.queryRenderedFeatures(e.point);
      if (features.length === 0) {
        if (this.selected.length) {
          const deselected = [...this.selected];
          this.selected = [];
          this.emit('selectionchange', { selected: [], deselected });
        }
        return;
      }
      const picked = this.options.multi ? features : [features[0]];
      const deselected = this.options.multi ? [] : this.selected;
      this.selected = this.options.multi ? [...this.selected, ...picked] : picked;
      this.emit('select', { features: picked, deselected });
    });
  }

  getSelected(): any[] { return [...this.selected]; }

  clearSelection(): void {
    const deselected = [...this.selected];
    this.selected = [];
    this.emit('selectionchange', { selected: [], deselected });
  }
}

export interface DrawInteractionOptions {
  type: 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehand';
  source?: any;
  maxPoints?: number;
  minPoints?: number;
}

export class DrawInteraction extends Interaction {
  private type: string;
  private points: [number, number][] = [];
  private options: DrawInteractionOptions;
  private freehandDrawing = false;
  private restoreDragPan: (() => void) | null = null;

  constructor(options: DrawInteractionOptions) {
    super();
    this.type = options.type;
    this.options = options;
  }

  getType(): string { return this.type; }

  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    ml.getCanvas().style.cursor = 'crosshair';
    this._listenML('click', (e: any) => {
      if (!this.active) return;
      // Freehand is collected from pointer movement, not sparse click points.
      if (this.type === 'freehand') return;
      this.points.push([e.lngLat.lng, e.lngLat.lat]);
      this.emit('drawpoint', { coordinate: [e.lngLat.lng, e.lngLat.lat], points: [...this.points] });
      if (this.type === 'point' || ((this.type === 'circle' || this.type === 'rectangle') && this.points.length >= 2) ||
          (this.options.maxPoints && this.points.length >= this.options.maxPoints)) {
        this.finishDrawing();
      }
    });
    this._listenML('dblclick', (e: any) => {
      if (this.points.length >= (this.options.minPoints ?? 2)) {
        this.finishDrawing();
        try { e.preventDefault?.(); } catch {}
      }
    });
    if (this.type === 'freehand') {
      const begin = (e: any) => {
        if (!this.active) return;
        if (this.freehandDrawing) return;
        const wasEnabled = ml.dragPan?.isEnabled?.() ?? false;
        if (wasEnabled) {
          ml.dragPan.disable?.();
          this.restoreDragPan = () => { try { ml.dragPan.enable?.(); } catch {} this.restoreDragPan = null; };
        }
        this.freehandDrawing = true;
        this.points = [[e.lngLat.lng, e.lngLat.lat]];
        this.emit('drawpoint', { coordinate: this.points[0], points: [...this.points] });
      };
      const extend = (e: any) => {
        if (!this.active || !this.freehandDrawing) return;
        const point: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const previous = this.points[this.points.length - 1];
        // Avoid an excessive number of duplicate coordinates from high-rate events.
        if (!previous || previous[0] !== point[0] || previous[1] !== point[1]) {
          this.points.push(point);
          this.emit('drawpoint', { coordinate: point, points: [...this.points] });
        }
      };
      const end = () => {
        if (!this.freehandDrawing) return;
        this.freehandDrawing = false;
        if (this.points.length >= (this.options.minPoints ?? 3)) this.finishDrawing();
        else this.abortDrawing();
      };
      this._listenML('mousedown', begin); this._listenML('touchstart', begin);
      this._listenML('mousemove', extend); this._listenML('touchmove', extend);
      this._listenML('mouseup', end); this._listenML('touchend', end);
      this._listenML('touchcancel', () => { this.freehandDrawing = false; this.abortDrawing(); });
    }
  }

  removeLastPoint(): void {
    if (this.points.length > 0) {
      this.points.pop();
      this.emit('drawpoint', { points: [...this.points] });
    }
  }

  abortDrawing(): void {
    this.points = [];
    this.freehandDrawing = false;
    this.restoreDragPan?.();
    this.emit('drawabort');
    const ml = this.map?._maplibreMap;
    if (ml) ml.getCanvas().style.cursor = '';
  }

  finishDrawing(): GeoJSON.Feature | null {
    if (this.points.length === 0) return null;
    let geometry: GeoJSON.Geometry;
    if (this.type === 'point') {
      geometry = { type: 'Point', coordinates: this.points[0] };
    } else if (this.type === 'line') {
      geometry = { type: 'LineString', coordinates: this.points };
    } else if (this.type === 'rectangle' && this.points.length >= 2) {
      const [a, b] = this.points;
      const west = Math.min(a[0], b[0]), east = Math.max(a[0], b[0]);
      const south = Math.min(a[1], b[1]), north = Math.max(a[1], b[1]);
      geometry = { type: 'Polygon', coordinates: [[[west, south], [east, south], [east, north], [west, north], [west, south]]] };
    } else if (this.type === 'circle' && this.points.length >= 2) {
      const [center, edge] = this.points;
      const latRadians = center[1] * Math.PI / 180;
      const dx = (edge[0] - center[0]) * 111320 * Math.cos(latRadians);
      const dy = (edge[1] - center[1]) * 110540;
      const radius = Math.sqrt(dx * dx + dy * dy);
      const ring: number[][] = [];
      for (let i = 0; i <= 64; i++) {
        const angle = (i / 64) * Math.PI * 2;
        ring.push([
          center[0] + (radius * Math.cos(angle)) / (111320 * Math.max(0.000001, Math.cos(latRadians))),
          center[1] + (radius * Math.sin(angle)) / 110540,
        ]);
      }
      geometry = { type: 'Polygon', coordinates: [ring] };
    } else {
      const ring = [...this.points];
      if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
        ring.push(ring[0]);
      }
      geometry = { type: 'Polygon', coordinates: [ring] };
    }
    const properties: Record<string, any> = {};
    if (this.type === 'circle' && this.points.length >= 2) {
      const center = this.points[0], edge = this.points[1];
      properties.shape = 'circle';
      properties.center = center;
      properties.radiusMeters = Math.sqrt(
        ((edge[0] - center[0]) * 111320 * Math.cos(center[1] * Math.PI / 180)) ** 2 +
        ((edge[1] - center[1]) * 110540) ** 2
      );
    }
    if (this.type === 'rectangle') properties.shape = 'rectangle';
    if (this.type === 'freehand') properties.shape = 'freehand';
    const feature: GeoJSON.Feature = { type: 'Feature', geometry, properties };
    this.emit('drawend', { feature });
    this.points = [];
    this.freehandDrawing = false;
    this.restoreDragPan?.();
    const ml = this.map?._maplibreMap;
    if (ml) ml.getCanvas().style.cursor = '';
    return feature;
  }

  destroy(): void {
    const ml = this.map?._maplibreMap;
    if (ml) ml.getCanvas().style.cursor = '';
    this.restoreDragPan?.();
    super.destroy();
  }
}

export interface ModifyInteractionOptions { features?: any[]; pixelTolerance?: number; }

export class ModifyInteraction extends Interaction {
  private options: ModifyInteractionOptions;
  private dragging: { feature: any; vertex: number } | null = null;

  constructor(options: ModifyInteractionOptions = {}) { super(); this.options = options; }

  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    this._listenML('mousedown', (e: any) => {
      if (!this.active || !this.options.features) return;
      const tol = this.options.pixelTolerance ?? 8;
      for (const feat of this.options.features) {
        const coords = (feat.geometry?.coordinates as any) || [];
        const flat: [number, number][] = Array.isArray(coords[0]) ? coords as any : [coords as any];
        for (let i = 0; i < flat.length; i++) {
          const px = ml.project(flat[i] as any);
          const dx = px.x - e.point.x, dy = px.y - e.point.y;
          if (dx * dx + dy * dy <= tol * tol) {
            this.dragging = { feature: feat, vertex: i };
            try { e.preventDefault?.(); } catch {}
            return;
          }
        }
      }
    });
    this._listenML('mousemove', (e: any) => {
      if (!this.dragging) return;
      const coords = this.dragging.feature.geometry.coordinates;
      if (Array.isArray(coords[0])) coords[this.dragging.vertex] = [e.lngLat.lng, e.lngLat.lat];
      else this.dragging.feature.geometry.coordinates = [e.lngLat.lng, e.lngLat.lat];
      this.emit('modify', { feature: this.dragging.feature });
    });
    this._listenML('mouseup', () => {
      if (this.dragging) { this.emit('modifyend', { feature: this.dragging.feature }); this.dragging = null; }
    });
  }
}

export interface TranslateInteractionOptions { features?: any[]; hitTolerance?: number; }

export class TranslateInteraction extends Interaction {
  private options: TranslateInteractionOptions;
  private dragging: { feature: any; offset: [number, number] } | null = null;
  constructor(options: TranslateInteractionOptions = {}) { super(); this.options = options; }
  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    this._listenML('mousedown', (e: any) => {
      if (!this.active || !this.options.features?.length) return;
      const f = this.options.features[0];
      this.dragging = { feature: f, offset: [e.lngLat.lng, e.lngLat.lat] };
    });
    this._listenML('mousemove', (e: any) => {
      if (!this.dragging) return;
      const dx = e.lngLat.lng - this.dragging.offset[0];
      const dy = e.lngLat.lat - this.dragging.offset[1];
      this.dragging.offset = [e.lngLat.lng, e.lngLat.lat];
      const c = this.dragging.feature.geometry.coordinates;
      const shift = (arr: any): any => {
        if (typeof arr[0] === 'number') return [arr[0] + dx, arr[1] + dy];
        return arr.map(shift);
      };
      this.dragging.feature.geometry.coordinates = shift(c);
      this.emit('translate', { feature: this.dragging.feature });
    });
    this._listenML('mouseup', () => { if (this.dragging) { this.emit('translateend', { feature: this.dragging.feature }); this.dragging = null; } });
  }
}

export interface SnapInteractionOptions { features?: any[]; pixelTolerance?: number; }

export class SnapInteraction extends Interaction {
  private options: SnapInteractionOptions;
  constructor(options: SnapInteractionOptions = {}) { super(); this.options = options; }
  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    this._listenML('mousemove', (e: any) => {
      if (!this.active || !this.options.features) return;
      const tol = this.options.pixelTolerance ?? 10;
      const allVerts: [number, number][] = [];
      for (const f of this.options.features) {
        const c = f.geometry?.coordinates;
        const collect = (arr: any) => {
          if (typeof arr[0] === 'number') allVerts.push(arr as [number, number]);
          else for (const a of arr) collect(a);
        };
        if (c) collect(c);
      }
      let best: [number, number] | null = null, bestD = tol * tol;
      for (const v of allVerts) {
        const px = ml.project(v as any);
        const dx = px.x - e.point.x, dy = px.y - e.point.y;
        if (dx * dx + dy * dy <= bestD) { bestD = dx * dx + dy * dy; best = v; }
      }
      if (best) this.emit('snap', { vertex: best });
    });
  }
}

export interface MeasureInteractionOptions {
  type: 'distance' | 'area' | 'bearing';
  units?: 'metric' | 'imperial' | 'nautical';
}

export class MeasureInteraction extends Interaction {
  private type: 'distance' | 'area' | 'bearing';
  private units: 'metric' | 'imperial' | 'nautical';
  private points: [number, number][] = [];
  constructor(options: MeasureInteractionOptions) {
    super(); this.type = options.type; this.units = options.units || 'metric';
  }
  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    ml.getCanvas().style.cursor = 'crosshair';
    this._listenML('click', (e: any) => {
      if (!this.active) return;
      this.points.push([e.lngLat.lng, e.lngLat.lat]);
      const value = this._compute();
      this.emit('measure', { points: [...this.points], value, units: this.units, type: this.type });
    });
    this._listenML('dblclick', (e: any) => {
      const value = this._compute();
      this.emit('measureend', { points: [...this.points], value, units: this.units, type: this.type });
      this.points = [];
      try { e.preventDefault?.(); } catch {}
    });
  }
  private _compute(): number {
    if (this.points.length < 2) return 0;
    if (this.type === 'distance') {
      let total = 0;
      for (let i = 1; i < this.points.length; i++) total += this._haversine(this.points[i - 1], this.points[i]);
      return this._convert(total);
    }
    if (this.type === 'bearing' && this.points.length >= 2) {
      const [lon1, lat1] = this.points[0]; const [lon2, lat2] = this.points[1];
      const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180;
      const Δλ = (lon2 - lon1) * Math.PI / 180;
      const y = Math.sin(Δλ) * Math.cos(φ2);
      const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
      return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
    }
    if (this.type === 'area') {
      let area = 0;
      const R = 6378137;
      for (let i = 0; i < this.points.length; i++) {
        const [lon1, lat1] = this.points[i];
        const [lon2, lat2] = this.points[(i + 1) % this.points.length];
        area += (lon2 - lon1) * Math.PI / 180 * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
      }
      return Math.abs(area * R * R / 2);
    }
    return 0;
  }
  private _haversine(a: [number, number], b: [number, number]): number {
    const R = 6371000;
    const φ1 = a[1] * Math.PI / 180, φ2 = b[1] * Math.PI / 180;
    const Δφ = (b[1] - a[1]) * Math.PI / 180;
    const Δλ = (b[0] - a[0]) * Math.PI / 180;
    const x = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }
  private _convert(meters: number): number {
    if (this.units === 'imperial') return meters * 3.28084;
    if (this.units === 'nautical') return meters / 1852;
    return meters;
  }
  destroy(): void {
    const ml = this.map?._maplibreMap;
    if (ml) ml.getCanvas().style.cursor = '';
    super.destroy();
  }
}

export interface ContextMenuItem {
  text: string;
  callback: (e: { coordinate: [number, number]; feature?: any }) => void;
  separator?: boolean;
  disabled?: boolean | ((e: any) => boolean);
}

export class ContextMenuInteraction extends Interaction {
  private items: ContextMenuItem[];
  private menu: HTMLElement | null = null;

  constructor(options: { items: ContextMenuItem[] }) { super(); this.items = options.items; }
  addItem(i: ContextMenuItem): void { this.items.push(i); }
  removeItem(text: string): void { this.items = this.items.filter(x => x.text !== text); }
  setItems(i: ContextMenuItem[]): void { this.items = i; }

  protected attach(): void {
    const ml = this.map?._maplibreMap;
    if (!ml) return;
    this._listenML('contextmenu', (e: any) => {
      if (!this.active) return;
      try { e.preventDefault?.(); } catch {}
      this._showMenu(e);
    });
    this._listenML('click', () => this._hideMenu());
  }

  private _showMenu(e: any): void {
    this._hideMenu();
    const m = document.createElement('div');
    Object.assign(m.style, {
      position: 'absolute', left: `${e.point.x}px`, top: `${e.point.y}px`,
      background: 'white', border: '1px solid #ccc', borderRadius: '4px',
      padding: '4px 0', fontSize: '13px', zIndex: '100', minWidth: '120px',
    } as any);
    for (const it of this.items) {
      if (it.separator) { const hr = document.createElement('hr'); m.appendChild(hr); continue; }
      const row = document.createElement('div');
      row.textContent = it.text;
      Object.assign(row.style, { padding: '4px 12px', cursor: 'pointer' } as any);
      const disabled = typeof it.disabled === 'function' ? it.disabled(e) : !!it.disabled;
      if (disabled) { row.style.opacity = '0.5'; row.style.cursor = 'not-allowed'; }
      else row.onclick = () => { it.callback({ coordinate: [e.lngLat.lng, e.lngLat.lat] }); this._hideMenu(); };
      m.appendChild(row);
    }
    this.menu = m;
    this.map?._maplibreMap?.getContainer().appendChild(m);
  }

  private _hideMenu(): void {
    if (this.menu) { try { this.menu.remove(); } catch {} this.menu = null; }
  }

  destroy(): void { this._hideMenu(); super.destroy(); }
}
